// Rules for VALIDATION_CONSTRAINTS.md section 11 — Scripts / Resources.
//
// Editor state: generateWorkflowProject() (pssHelper.ts ~L755) currently
// emits an empty Resources dict. No node data field yet holds a persisted
// Diagram.Resources map, so most resource-level rules (11.1, 11.2, 11.3, 11.8)
// are gated: they only execute against a ProcessDefinition.resources field
// (if one exists at runtime) or node.data.inputScripts/outputScripts/exportScripts
// fields. If the fields are absent, the rules are effectively no-ops.
//
// Peak-specific divergences:
//   - 11.4: Peak supports both JavaScript and Python (pssHelper.ts getScriptKind
//     lines 90-93). The constraint doc states "JS-compatible only" — we treat
//     this as a Peak extension and accept {undefined, 'javascript', 'js',
//     'python'}. Any other string is an error.
import { registerRule } from '../registry';
import { ValidationRule, ValidationIssue } from '../types';
import { getFamily, labelOf } from '../helpers';

// Shape for Diagram.Resources map on a process; editor does not persist this
// yet but rules must be resilient when the engine loads a diagram that does.
type ResourceEntry = { Kind?: string | number; kind?: string | number };
type ProcessWithResources = { resources?: Record<string, ResourceEntry> };
// Node.data fields referenced by section-11 rules.
type NodeDataWithScripts = {
  inputScripts?: unknown;
  outputScripts?: unknown;
  exportScripts?: unknown;
  scriptLanguage?: string;
  exportAll?: boolean;
};

const ALLOWED_SCRIPT_LANGUAGES: ReadonlySet<string | undefined> = new Set<
  string | undefined
>([undefined, 'javascript', 'python', 'js']);

const rule_11_1_ResourceIdUniqueness: ValidationRule = {
  id: '11.1',
  description:
    'DiagramResource ids must be unique per diagram (no-op until resources persist)',
  scope: 'diagram',
  run: (ctx) => {
    const issues: ValidationIssue[] = [];
    // The editor does not yet persist a diagram-level resources map. Check
    // any process.resources present at runtime; otherwise pass.
    const seen = new Map<string, string[]>();
    for (const p of ctx.processes) {
      const resources = (p as ProcessWithResources).resources;
      if (!resources || typeof resources !== 'object') continue;
      for (const key of Object.keys(resources)) {
        const list = seen.get(key) ?? [];
        list.push(p.name || p.id);
        seen.set(key, list);
      }
    }
    for (const [key, procs] of seen) {
      if (procs.length > 1) {
        issues.push({
          severity: 'error',
          rule: '11.1',
          message: `Resource id '${key}' is duplicated across processes: ${procs.join(', ')}.`,
        });
      }
    }
    return issues;
  },
};
registerRule(rule_11_1_ResourceIdUniqueness);

const rule_11_2_ScriptResourceResolution: ValidationRule = {
  id: '11.2',
  description:
    'InputScripts/OutputScripts/ExportScripts ids must resolve to a diagram-level resource entry',
  scope: 'diagram',
  run: (ctx) => {
    const issues: ValidationIssue[] = [];
    // Build the union of resource keys from any process.resources maps.
    const allKeys = new Set<string>();
    let resourcesPersist = false;
    for (const p of ctx.processes) {
      const resources = (p as ProcessWithResources).resources;
      if (resources && typeof resources === 'object') {
        resourcesPersist = true;
        for (const key of Object.keys(resources)) allKeys.add(key);
      }
    }

    for (const p of ctx.processes) {
      for (const n of p.nodes) {
        const d = n.data as NodeDataWithScripts | undefined;
        if (!d) continue;
        const lists: Array<[string, unknown]> = [
          ['inputScripts', d.inputScripts],
          ['outputScripts', d.outputScripts],
          ['exportScripts', d.exportScripts],
        ];
        for (const [fieldName, value] of lists) {
          if (!Array.isArray(value) || value.length === 0) continue;
          if (!resourcesPersist) {
            issues.push({
              severity: 'warning',
              rule: '11.2',
              message: `${labelOf(n)}: ${fieldName} references resources but the editor does not yet persist Diagram.Resources — IDs will not resolve.`,
              processId: p.id,
              nodeIds: [n.id],
            });
            continue;
          }
          for (const id of value) {
            const key = String(id);
            if (!allKeys.has(key)) {
              issues.push({
                severity: 'error',
                rule: '11.2',
                message: `${labelOf(n)}: ${fieldName} references unknown resource id '${key}'.`,
                processId: p.id,
                nodeIds: [n.id],
              });
            }
          }
        }
      }
    }
    return issues;
  },
};
registerRule(rule_11_2_ScriptResourceResolution);

const rule_11_3_ResourceKind: ValidationRule = {
  id: '11.3',
  description:
    'Resource Kind must be in {Expression, Template, InlineScript} (gated on resources persisting)',
  scope: 'diagram',
  run: (ctx) => {
    const issues: ValidationIssue[] = [];
    const allowed = new Set(['Expression', 'Template', 'InlineScript']);
    for (const p of ctx.processes) {
      const resources = (p as ProcessWithResources).resources;
      if (!resources || typeof resources !== 'object') continue;
      for (const [key, res] of Object.entries(resources)) {
        const kind = res?.Kind ?? res?.kind;
        // Numeric Kind values are used by the engine — defer to a future
        // lookup; accept when not a string. Only enforce when a string name
        // is present.
        if (typeof kind !== 'string') continue;
        if (!allowed.has(kind)) {
          issues.push({
            severity: 'error',
            rule: '11.3',
            message: `Resource '${key}' has invalid Kind '${kind}'. Expected one of ${Array.from(allowed).join(', ')}.`,
          });
        }
      }
    }
    return issues;
  },
};
registerRule(rule_11_3_ResourceKind);

const rule_11_4_ScriptLanguage: ValidationRule = {
  id: '11.4',
  description:
    'node.data.scriptLanguage must be undefined or one of {javascript, js, python} — Peak divergence from JS-only doc',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      const d = n.data as NodeDataWithScripts | undefined;
      if (!d) continue;
      const lang = d.scriptLanguage;
      if (!ALLOWED_SCRIPT_LANGUAGES.has(lang)) {
        issues.push({
          severity: 'error',
          rule: '11.4',
          message: `${labelOf(n)} has unsupported scriptLanguage '${lang}'. Allowed: javascript, js, python.`,
          processId: process.id,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_11_4_ScriptLanguage);

const rule_11_5_EndEventExportAllWithScripts: ValidationRule = {
  id: '11.5',
  description:
    'EndEvent with ExportAll=true and non-empty ExportScripts — scripts will not run',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (getFamily(n.type) !== 'EndEvent') continue;
      const d = n.data as NodeDataWithScripts | undefined;
      if (!d) continue;
      const exportAll = d.exportAll === true;
      const exportScripts = d.exportScripts;
      const hasScripts = Array.isArray(exportScripts) && exportScripts.length > 0;
      if (exportAll && hasScripts) {
        issues.push({
          severity: 'warning',
          rule: '11.5',
          message: `${labelOf(n)}: ExportScripts ignored when ExportAll is true.`,
          processId: process.id,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_11_5_EndEventExportAllWithScripts);
